import { Controller } from "@hotwired/stimulus";
import { debounce } from "../helpers/timing_helpers";
import {
  hasPreloadedEmojiImage,
  preloadEmojiImage
} from "../helpers/emoji_image_cache";
// Global cache so Quill loads only one time
let quillLibs = null;

globalThis.__linkPreviewInFlight ||= {};

// Register custom emoji blot after Quill is loaded
function registerEmojiBlot(Quill) {
  const Embed = Quill.import("blots/embed");

  class EmojiBlot extends Embed {
    static create(value) {
      const node = super.create();
      const nativeEmoji = value.native || nativeEmojiFromUnified(value.unified) || "🙂";

      node.dataset.emoji = value.unified;
      node.dataset.native = nativeEmoji;
      node.dataset.url = value.url;

      Object.assign(node.style, {
        display: "inline-block",
        width: "20px",
        height: "20px",
        lineHeight: "20px",
        verticalAlign: "middle",
        textAlign: "center",
        position: "relative",
        overflow: "hidden"
      });

      const textSpan = document.createElement("span");
      textSpan.textContent = nativeEmoji;

      Object.assign(textSpan.style, {
        display: "inline-block",
        width: "20px",
        height: "20px",
        lineHeight: "20px",
        fontSize: "20px",
        verticalAlign: "top",
        overflow: "hidden",
        visibility: value.url ? "hidden" : "visible"
      });

      node.appendChild(textSpan);

      if (value.url) {
        const showImage = () => {
          node.style.backgroundImage = `url(${value.url})`;
          node.style.backgroundSize = "20px 20px";
          node.style.backgroundRepeat = "no-repeat";
          node.style.backgroundPosition = "center";
          textSpan.style.visibility = "hidden";
          textSpan.classList.add("opacity-0");

          node
            .closest(".ql-editor")
            ?.dispatchEvent(
              new CustomEvent("emoji-image-loaded", { bubbles: true })
            );
        };

        const showNativeEmoji = () => {
          node.style.backgroundImage = "";
          textSpan.style.visibility = "visible";
          textSpan.classList.remove("opacity-0");
        };

        if (hasPreloadedEmojiImage(value.url)) {
          showImage();
        } else {
          preloadEmojiImage(value.url).then((loaded) => {
            if (!node.isConnected) return;
            if (loaded) {
              showImage();
            } else {
              showNativeEmoji();
            }
          });
        }
      }

      return node;
    }

    static value(node) {
      return {
        unified: node.dataset.emoji,
        native: node.dataset.native || "",
        url: node.dataset.url
      };
    }
  }

  EmojiBlot.blotName = "emoji";
  EmojiBlot.tagName = "span";
  EmojiBlot.className = "ql-emoji";

  Quill.register(EmojiBlot, true);
}

export default class QuillController extends Controller {
  static targets = ["createPost"];
  static values = {
    imageEvent: String,
    // Opt out on an individual editor with data-quill-emoji-shortcuts-value="false".
    emojiShortcuts: { type: Boolean, default: true }
  };

  // Load heavy libraries dynamically
  async loadLibs() {
    if (quillLibs) return quillLibs;

    const QuillModule = await import("quill");
    const TributeModule = await import("tributejs");
    const { findEmojiShortcuts } = await import("../helpers/emoji_shortcuts");

    const Quill = QuillModule.default || QuillModule.Quill || QuillModule;
    const Tribute = TributeModule.default || TributeModule;

    registerEmojiBlot(Quill);
    const Delta = Quill.import("delta");
    quillLibs = { Quill, Tribute, Delta, findEmojiShortcuts };
    return quillLibs;
  }

  connect() {
    this.setupQuill();
  }

  async setupQuill() {
    const { Quill, Tribute, Delta, findEmojiShortcuts } = await this.loadLibs();

    this.findEmojiShortcuts = findEmojiShortcuts;

    this.Quill = Quill;
    this.Tribute = Tribute;
    this.Delta = Delta;
    this.index = 0;
    this.maxLimit = this.element?.dataset?.limit || 280;

    this.fetchMentionUsers = debounce(this.fetchMentionUsers.bind(this), 400);
    this.fetchHashtags = debounce(this.fetchHashtags.bind(this), 400);

    this.configureQuillFormats();

    const element = this.element;
    const dataName = element.dataset.name;
    const toolbarConfig = this.toolbarConfig(element, dataName);
    this.basePlaceholder = element.dataset.placeholder || "";
    this.placeholderSource = element.parentElement?.querySelector(
      ".quill-placeholder-translation-source"
    );
    const placeholder = this.currentPlaceholder();

    const quill = new Quill(element, {
      theme: "snow",
      placeholder,
      modules: {
        toolbar: toolbarConfig.length === 0 ? false : toolbarConfig
      },
      // Links and emoji embeds must be allowed even without a toolbar.
      formats: ["emoji", "link"]
    });

    this.quill = quill;
    this.handleLanguageChange = this.languageChanged.bind(this);
    document.addEventListener(
      "itoons:language-change",
      this.handleLanguageChange
    );

    quill.root.addEventListener("tribute-replaced", (e) => {
      requestAnimationFrame(() => {
        const range = quill.selection.getRange()?.[0];

        if (!range) return;

        const value =
          e.detail.item.original.display_name ||
          e.detail.item.original.name;

        quill.setSelection(range.index + value.length + 1, 0, "silent");
      });
    });

    this.observePlaceholderTranslation();
    this.setupLinkThumbObserver();
    quill.root.addEventListener(
      "drop",
      (event) => {
        // Send dropped image(s) to the file input instead of inserting them into the editor.
        const files = [...(event.dataTransfer?.files || [])];
        const imageFiles = files.filter(file =>
          file.type.startsWith("image/")
        );

        if (imageFiles.length > 0) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          this.dispatchImages(imageFiles);
          return;
        }

        // Firefox may provide dragged images as HTML instead of files.
        const html = event.dataTransfer?.getData("text/html");

        if (html) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const img =
            doc.querySelector(".main-user-img") ||
            doc.querySelector("img.main-user-img") ||
            [...doc.querySelectorAll("img")].at(-1);

          if (img?.src) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            // If this is an actual remote image URL, fetch it and turn it into a File so it behaves like Chrome's drag/drop.
            this.imageUrlToFile(img.src)
              .then((file) => {
                if (file) {
                  this.dispatchImages([file]);
                }
              })
              .catch((error) => {
                console.error("Failed to process dropped image:", error);
              });

            return;
          }
        }

        const uri = event.dataTransfer?.getData("text/uri-list");
        const text = event.dataTransfer?.getData("text/plain");
        const url = uri || text;

        if (!url || !/^https?:\/\//i.test(url)) {
          return;
        }

        // Do NOT allow /messages/... URLs to enter Quill.
        if (isIgnoredMessageUrl(url)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation?.();
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const range = quill.getSelection(true) || {
          index: quill.getLength(),
          length: 0
        };

        quill.insertText(
          range.index,
          url,
          {
            link: url
          },
          "user"
        );

        quill.setSelection(range.index + url.length, 0, "silent");

        setTimeout(() => {
          this.scheduleLinkPreview(quill, 0);
        }, 100);
      },
      true
    );

    quill.blur();
    for (const link of quill.root.querySelectorAll("a.tribute-inserted")) {
      link.classList.add("align-items-center", "text-primary");
      link.dataset.mention = "true";
      link.dataset.name = link.textContent.trim();
      link.setAttribute("target", "_blank");
    }

    this.dispatchProgressUpdate(quill);
    this.dispatchContentStateUpdate(quill);
    this.setupLinkSanitizer();
    this.setupHiddenInput(element, dataName);
    this.setupKeyboard(quill);
    this.setupCreatePostEvents(quill);
    this.setupPasteHandler(quill);
    this.setupTextChange(quill);
    this.setupEmojiEvents(quill);
    this.focusEditor(quill);
  }

  currentPlaceholder() {
    const language = localStorage.getItem("selected_lang") || "en";
    const source = this.placeholderSource?.textContent?.trim();
    if (this.placeholderSource && language !== "en") {
      // Google translates this live text node, unlike the editor's data attribute.
      return source && source !== this.basePlaceholder ? source : "";
    }
    return this.basePlaceholder;
  }

  observePlaceholderTranslation() {
    if (!this.placeholderSource) return;

    this.placeholderObserver = new MutationObserver(() => {
      this.syncPlaceholder();
    });

    this.placeholderObserver.observe(this.placeholderSource, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  syncPlaceholder() {
    if (!this.quill?.root) return;

    this.quill.root.dataset.placeholder = this.currentPlaceholder();
  }

  async imageUrlToFile(url) {
    const response = await fetch(url, {
      credentials: "include"
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();

    const parsedUrl = new URL(url, globalThis.location.href);

    let filename = parsedUrl.pathname.split("/").findLast(Boolean);

    if (!filename?.includes(".")) {
      const extensionMap = {
        "image/jpeg": "jpg",
        "image/png": "png",
        "image/gif": "gif",
        "image/webp": "webp",
        "image/svg+xml": "svg",
        "image/avif": "avif"
      };

      const extension =
        extensionMap[blob.type] || "jpg";

      filename = `pasted-image.${extension}`;
    }

    return new File(
      [blob],
      filename,
      {
        type: blob.type || "image/jpeg"
      }
    );
  }

  languageChanged() {
    this.syncPlaceholder();
  }

  // Quill format config
  configureQuillFormats() {
    const Quill = this.Quill;

    const fontSizeArr = [
      "8px",
      "9px",
      "10px",
      "12px",
      "14px",
      "16px",
      "20px",
      "24px",
      "32px",
      "42px",
      "54px",
      "68px",
      "84px",
      "98px"
    ];

    const fontFamilyArr = [
      "",
      "sans-serif",
      "aboreto",
      "montserrat",
      "roboto",
      "explora",
      "times-new-roman",
      "arial"
    ];

    const Size = Quill.import("attributors/style/size");
    Size.whitelist = fontSizeArr;
    Quill.register(Size, true);

    const Font = Quill.import("formats/font");
    Font.whitelist = fontFamilyArr;
    Quill.register(Font, true);

    const BackgroundClass = Quill.import("attributors/class/background");
    const ColorClass = Quill.import("attributors/class/color");

    Quill.register(BackgroundClass, true);
    Quill.register(ColorClass, true);
  }

  // Toolbar config
  toolbarConfig(element, dataName) {
    const toolbar = element.dataset.toolbar;

    if (toolbar !== undefined && toolbar !== "true") return [];

    if (dataName === "social_comment[comment]") {
      return ["emoji"];
    }

    return [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote", "emoji"]
    ];
  }

  // Link sanitizer
  setupLinkSanitizer() {
    const Link = this.Quill.import("formats/link");
    const builtInFunc = Link.sanitize;

    Link.sanitize = function customSanitizeLinkInput(linkValueInput) {
      let val = linkValueInput;

      if (!/^\w+:/.test(val) && !/^https?:/.test(val)) {
        val = "http:" + val;
      }

      return builtInFunc.call(this, val);
    };
  }

  // Hidden input for form submit
  setupHiddenInput(element, dataName) {
    const hiddenInput = document.createElement("input");

    hiddenInput.type = "hidden";
    hiddenInput.id =
      element.dataset.first === "true" && element.dataset.id !== ""
        ? element.dataset.id
        : "not_ignore";
    hiddenInput.name = dataName;
    hiddenInput.value = element.dataset.value ?? "";
    hiddenInput.required = element.dataset.required != null;
    let otherAttr = element.dataset.hiddenInputAttrs;
    if (otherAttr) {
      try {
        otherAttr = JSON.parse(otherAttr);

        for (const [key, value] of Object.entries(otherAttr)) {
          hiddenInput.setAttribute(key, value);
        }
      } catch (e) {
        console.error("Invalid JSON in data-hidden-input-attrs", e);
      }
    }
    this.hiddenInput = hiddenInput;
    element.parentElement.appendChild(hiddenInput);
  }

  setupKeyboard(quill) {
    quill.keyboard.addBinding({ key: 13 }, () => {
      this.element.dispatchEvent(
        new KeyboardEvent("keydown", { key: "Enter", bubbles: true })
      );
      return false;
    });
  }

  setupCreatePostEvents(quill) {
    if (!this.hasCreatePostTarget) return;

    $(this.element).on("click.quillController", () => {
      const $modal = $(this.element).closest("#addPostModal");

      if ($modal.length > 0) {
        $modal.find(".post_media").addClass("d-none");
        $modal.find("#post-media-" + $(this.element).attr("data-index"))
              .parent()
              .removeClass("d-none");
      }
    });

    $(this.element).on("keyup.quillController", (event) => {
      this.dispatchContentStateUpdate(quill);

      if (event.which === 13) {
        event.preventDefault();
        return false;
      }
    });

    this.mentionUser(quill);
  }

  setupPasteHandler(quill) {
    this._pasteHandler = async (event) => {
      // Send pasted image(s) to the file input instead of inserting them into the editor.
      const clipboard = event.clipboardData;

      if (!clipboard) return;

      const items = Array.from(clipboard.items || []);

      const imageFiles = items
        .filter(item => item.kind === "file" && item.type.startsWith("image/"))
        .map(item => item.getAsFile())
        .filter(Boolean);

      if (imageFiles.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.dispatchImages(imageFiles);
        return;
      }

      const html = clipboard.getData("text/html");

      if (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const img =
          doc.querySelector("img.main-user-img") ||
          doc.querySelector(".main-user-img img") ||
          doc.querySelector("img");

        if (img) {
          let imageUrl =
            img.getAttribute("src") ||
            img.dataset.src ||
            img.dataset.original;

          if (!imageUrl) {
            const srcset = img.getAttribute("srcset");

            if (srcset) {
              imageUrl = srcset
                .split(",")
                .map(value => value.trim().split(/\s+/)[0])
                .findLast(Boolean);
            }
          }

          if (imageUrl) {
            try {
              imageUrl = new URL(
                imageUrl,
                document.baseURI
              ).href;
            } catch {
              imageUrl = null;
            }
          }

          if (
            imageUrl &&
            !imageUrl.startsWith("data:") &&
            !imageUrl.startsWith("blob:")
          ) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            try {
              const file = await this.imageUrlToFile(imageUrl);

              if (file) {
                this.dispatchImages([file]);
                return;
              }
            } catch (error) {
              console.error("Failed to convert pasted image to file:", error);
            }
          }
        }
      }
      const clipboardText = clipboard.getData("text/plain");

      if (!clipboardText) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      const range = quill.getSelection(true) || {
        index: Math.max(quill.getLength() - 1, 0),
        length: 0
      };

      const text = this.normalizedPasteText(clipboardText, quill, range);
      if (!text) return;

      const formats = {
        ...quill.getFormat(range)
      };

      // Do not continue the old link format
      delete formats.link;

      const contentDelta = this.buildPasteDelta(
        text,
        formats
      );

      let updateDelta = new this.Delta()
        .retain(range.index);

      if (range.length > 0) {
        updateDelta = updateDelta.delete(range.length);
      }

      updateDelta = updateDelta.concat(contentDelta);

      quill.updateContents(updateDelta, "user");

      requestAnimationFrame(() => {
        quill.focus();

        quill.setSelection(
          range.index + contentDelta.length(),
          0,
          "silent"
        );

        quill.format("link", false, "silent");
      });
    };

    quill.root.addEventListener(
      "paste",
      this._pasteHandler,
      true
    );
  }

  buildPasteDelta(text, formats = {}) {
    const delta = new this.Delta();
    const urlRegex = /https?:\/\/[^\s]+/gi;

    let lastIndex = 0;

    for (const match of text.matchAll(urlRegex)) {
      const beforeUrl = text.slice(
        lastIndex,
        match.index
      );

      if (beforeUrl) {
        this.appendTextAndEmojis(
          delta,
          beforeUrl,
          formats
        );
      }

      const url = match[0];

      if (isIgnoredMessageUrl(url)) {
        lastIndex = match.index + url.length;
        continue;
      }


      delta.insert(url, {
        ...formats,
        link: url
      });

      lastIndex = match.index + url.length;
    }

    const remainingText = text.slice(lastIndex);

    if (remainingText) {
      this.appendTextAndEmojis(
        delta,
        remainingText,
        formats
      );
    }

    return delta;
  }

  normalizedPasteText(text, quill, range) {
    const normalized = text
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .map((line) => line.trimEnd())
      .join("\n");

    let end = normalized.length;

    while (end >= 2 && normalized[end - 1] === "\n" && normalized[end - 2] === "\n") {
      end--;
    }

    return normalized.slice(0, end);

  }

  appendTextAndEmojis(delta, text, formats = {}) {
    let textBuffer = "";

    const flushTextBuffer = () => {
      if (!textBuffer) return;

      delta.insert(textBuffer, formats);
      textBuffer = "";
    };

    for (const segment of this.segments(text)) {
      if (!isEmojiChar(segment)) {
        textBuffer += segment;
        continue;
      }

      flushTextBuffer();

      const unified = getUnified(segment);

      delta.insert({
        emoji: {
          unified,
          native: segment,
          url: emojiUrl(unified)
        }
      });
    }

    flushTextBuffer();
  }

  setupTextChange(quill) {
    // const element = this.element;:
    // const hiddenInput = this.hiddenInput;

    this.dispatchContentStateUpdate(quill);

    quill.on("text-change", (delta, oldDelta, source) => {
      if (this.isConverting) return;
      if (source === "user" && this.characterLength(quill) > this.maxLimit) {
        alert(`Character limit exceeded. Maximum allowed: ${this.maxLimit} characters.`);
        quill.history.undo();
        return;
      }

      if (source === "user") {
        this.convertTextToEmoji(quill, delta);
        this.autoLinkUrls(quill);
      }

      this.handleTextChange(quill);
    });

    quill.root.addEventListener("beforeinput", (e) => {
      if (!e.data) return;

      const range = quill.getSelection();
      if (!range) return;

      const currentLength = this.characterLength(quill);
      const replacing = range.length;

      if (currentLength - replacing + this.segments(e.data).length > this.maxLimit) {
        e.preventDefault();
        alert(`Character limit exceeded. Maximum allowed: ${this.maxLimit} characters.`);
      }
    });

    quill.on("selection-change", (range) => {
      for (const el of quill.root.querySelectorAll(".ql-emoji")) {
        el.classList.remove("emoji-selected");
      }

      if (!range || range.length === 0) return;

      for (const node of quill.root.querySelectorAll(".ql-emoji")) {
        const blot = this.Quill.find(node);
        if (!blot) continue;

        const index = quill.getIndex(blot);

        if (index >= range.index && index < range.index + range.length) {
          node.classList.add("emoji-selected");
        }
      }
    });
  }

  handleTextChange(quill) {
    const element = this.element;
    const hiddenInput = this.hiddenInput;
    const length = this.characterLength(quill);

    this.dispatchProgressUpdate(quill);
    this.dispatchContentStateUpdate(quill);

    setTimeout(() => {
      this.updateEditedLinks(quill);

      if (!this.isPasting) {
        this.scheduleLinkPreview(quill);
      }

      const html = this.buildHiddenInputValue(quill, element);

      if (html === null) {
        hiddenInput.value = "";
        return;
      }

      hiddenInput.value = html;
      hiddenInput.dataset.length = length;

      this.revalidateHiddenInput(quill, hiddenInput);
      this.dispatchInputEvents(hiddenInput);
    }, 0);
  }

  updateEditedLinks(quill) {
    const Quill = this.Quill;

    for (const link of quill.root.querySelectorAll("a")) {
      if (
        link.classList.contains("tribute-inserted") ||
        link.classList.contains("atwho-inserted")
      ) {
        continue;
      }

      const text = link.textContent.trim();
      const blot = Quill.find(link);

      if (!blot) continue;

      const index = quill.getIndex(blot);

      if (/^https?:\/\/\S+$/i.test(text)) {
        this.updateLinkHref(quill, link, index, text);
      } else {
        quill.formatText(
          index,
          link.textContent.length,
          "link",
          false,
          "silent"
        );
      }
    }
  }

  updateLinkHref(quill, link, index, text) {
    const currentHref = link.getAttribute("href");

    if (currentHref !== text) {
      quill.formatText(
        index,
        text.length,
        "link",
        text,
        "silent"
      );
    }
  }

  buildHiddenInputValue(quill, element) {
    const html = quill.root.innerHTML.trim();

    if (element.dataset.name === "comment[description]") {
      return this.buildCommentDescriptionValue(quill);
    }

    return this.cleanQuillHtml(html);
  }

  buildCommentDescriptionValue(quill) {
    const text = quill.root.textContent.replaceAll("\u00a0", " ");

    if (!/\S/.test(text)) {
      return null;
    }

    return quill.root.innerHTML.trim();
  }

  cleanQuillHtml(html) {
    const emptyParagraph = String.raw`\s*<p>(?:\s*|<br\s*/?>)*<\/p>`;

    html = html.replace(
      new RegExp(
        `(?:^(?:${emptyParagraph})+|(?:${emptyParagraph})+$)`,
        "gi"
      ),
      ""
    );

    return html.replace(/\s+/g, " ").trim();
  }

  revalidateHiddenInput(quill, hiddenInput) {
    requestAnimationFrame(() => {
      const field = $(hiddenInput);
      const isPlaceholderVisible =
        quill.root.classList.contains("ql-blank");

      if (isPlaceholderVisible) {
        field.removeClass("error");
        field.next("label.error").remove();
        return;
      }

      field.valid();
    });
  }

  dispatchInputEvents(hiddenInput) {
    hiddenInput.dispatchEvent(
      new Event("input", { bubbles: true })
    );

    $(hiddenInput).trigger("keyup");
  }

  scheduleLinkPreview(quill, delay = 120) {
    clearTimeout(this.linkPreviewTimer);

    const links = formatAndCollectLinks(quill, this.element) || [];
    const currentUrl = links.at(-1)?.href || "";
    const form = $(this.element).closest("form");
    const previewUrl = form.find("#thumb_url").val() || "";
    const hasPreview = form.find("#link-thumb").html()?.trim() !== "";

    // Keep an already generated preview when the author removes the raw URL
    // from the editor. The preview's close button remains the explicit way to
    // remove both the preview and its destination URL.
    if (!currentUrl && previewUrl && hasPreview) {
      this.linkPreviewTimer = null;
      return;
    }

    // Invalidate the old request and preview immediately. This ensures a
    // response for the URL that existed before Backspace cannot render while
    // the user is still deleting it.
    if (previewUrl !== currentUrl) {
      form.find("#thumb_url").val(currentUrl);
      form.find("#link-thumb").empty();
      form.find("#link_preview").val(false);
    }

    if (!currentUrl) {
      this.linkPreviewTimer = null;
      return;
    }

    this.linkPreviewTimer = setTimeout(() => {
      this.linkPreviewTimer = null;
      handleLinks(quill, this.element);
    }, delay);
  }

  setupEmojiEvents(quill) {
    quill.root.addEventListener("emoji-image-loaded", () => {
      $(this.element).trigger("keyup");
      this.hiddenInput.value = quill.root.innerHTML;
    });

    this._emojiCopyHandler = (event) => {
      const range = quill.getSelection();
      if (!range || range.length === 0 || !event.clipboardData) return;

      const operations = quill.getContents(range.index, range.length).ops;
      const containsEmoji = operations.some((operation) => operation.insert?.emoji);
      if (!containsEmoji) return;

      const text = operations.map((operation) => {
        if (typeof operation.insert === "string") return operation.insert;

        const emoji = operation.insert?.emoji;
        if (!emoji) return "";

        return emoji.native || nativeEmojiFromUnified(emoji.unified);
      }).join("");

      const html = document.createElement("div");
      html.textContent = text;

      event.preventDefault();
      event.clipboardData.setData("text/plain", text);
      event.clipboardData.setData("text/html", html.innerHTML.replaceAll("\n", "<br>"));
    };

    quill.root.addEventListener("copy", this._emojiCopyHandler);

    this._nativeSelectionHandler = () => {
      const selection = globalThis.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

      const range = selection.getRangeAt(0);
      const selectionStartsInside = quill.root.contains(selection.anchorNode);
      const selectionEndsInside = quill.root.contains(selection.focusNode);
      if (!selectionStartsInside && !selectionEndsInside) return;

      for (const emoji of quill.root.querySelectorAll(".ql-emoji")) {
        let selected = false;

        try {
          selected = range.intersectsNode(emoji);
        } catch {
          selected = false;
        }

        emoji.classList.toggle("emoji-selected", selected);
      }
    };

    document.addEventListener("selectionchange", this._nativeSelectionHandler);

    const isAndroid = /Android/i.test(navigator.userAgent);
    let androidFramePending = false;
    const androidSafeUpdate = () => {
      if (androidFramePending) return;

      androidFramePending = true;
      requestAnimationFrame(() => {
        androidFramePending = false;
        // Fix Android issue where Quill placeholder does not disappear while typing
        const editor = quill.root;
        const isReallyEmpty = editor.innerHTML === "<p><br></p>";
        editor.classList.toggle("ql-blank", isReallyEmpty);

        // Fix the issue where the count does not increase while typing on Android phones.
        this.handleTextChange(quill);
      });
    };

    // Fix the Android event not fire issue
    if (isAndroid) {
      quill.root.addEventListener("input", androidSafeUpdate);
    }
  }

  focusEditor(quill) {
    setTimeout(() => {
      if (!this.element.isConnected) return;
      const chat = this.element.closest(".chat-messages");
      if (chat?.querySelector(".message_search_box:not(.d-none)")) return;

      if (
        globalThis.innerWidth > 576 &&
        this.element.dataset.focus !== undefined
      ) {
        quill.focus({ preventScroll: true });
      }
    }, 500);
  }

  convertTextToEmoji(quill, changeDelta) {
    const Delta = this.Delta;
    const replacements = [];
    const insertedRanges = [];
    let documentIndex = 0;
    let insertedContentEnd = null;

    for (const operation of changeDelta.ops) {
      if (operation.retain) documentIndex += operation.retain;
      if (operation.insert !== undefined) {
        const length = typeof operation.insert === "string" ? operation.insert.length : 1;
        if (typeof operation.insert === "string") {
          insertedRanges.push({ start: documentIndex, end: documentIndex + length });
          insertedContentEnd = documentIndex + length;
        }
        documentIndex += length;
      }
    }
    if (insertedRanges.length === 0) return;

    // Read surrounding text as shortcuts arrive one character at a time.
    // Embeds occupy one Quill position and must not shift subsequent matches.
    documentIndex = 0;
    for (const operation of quill.getContents().ops) {
      if (typeof operation.insert !== "string") {
        documentIndex += 1;
        continue;
      }

      const text = operation.insert;
      const formats = operation.attributes || {};
      const shortcuts = this.emojiShortcutsValue && this.findEmojiShortcuts && !formats.link && !formats.code && !formats["code-block"]
        ? this.findEmojiShortcuts(text) : [];
      const candidates = shortcuts.map(match => ({
        index: documentIndex + match.index,
        length: match.length,
        native: match.emoji.native,
        unified: match.emoji.unified
      }));
      let offset = 0;
      for (const segment of this.segments(text)) {
        if (isEmojiChar(segment)) {
          candidates.push({
            index: documentIndex + offset,
            length: segment.length,
            native: segment,
            unified: getUnified(segment)
          });
        }
        offset += segment.length;
      }
      for (const candidate of candidates) {
        const end = candidate.index + candidate.length;
        if (insertedRanges.some(range => candidate.index < range.end && end >= range.start)) {
          replacements.push(candidate);
        }
      }
      documentIndex += text.length;
    }
    if (replacements.length === 0) return;

    const replacementDelta = new Delta();
    let consumed = 0;
    for (const replacement of replacements.toSorted((a, b) => a.index - b.index)) {
      replacementDelta.retain(replacement.index - consumed)
        .delete(replacement.length)
        .insert({
          emoji: {
            unified: replacement.unified,
            native: replacement.native,
            url: emojiUrl(replacement.unified)
          }
        });
      consumed = replacement.index + replacement.length;
    }

    this.isConverting = true;
    try {
      quill.updateContents(replacementDelta, "silent");
      const cursorIndex = replacementDelta.transformPosition(insertedContentEnd);
      globalThis.cancelAnimationFrame(this.emojiSelectionFrame);
      this.emojiSelectionFrame = globalThis.requestAnimationFrame(() => {
        if (this.quill !== quill) return;
        quill.setSelection(cursorIndex, 0, "silent");
        this.emojiSelectionFrame = null;
      });
    } finally {
      this.isConverting = false;
    }
  }

  mentionUser(quill) {
    this.tribute = new this.Tribute({
      collection: [
        {
          trigger: "@",
          allowSpaces: true,
          lookup: "display_name",

          values: (query, callback) => {
            if (query.length < 1) return callback([]);
            this.fetchMentionUsers(query, callback);
          },

          menuItemTemplate: item => item.original.name,

          selectTemplate: item => {
            return `${item.original.display_name} `;
          }
        },
        {
          trigger: "#",
          allowSpaces: true,
          lookup: "name",

          values: (query, callback) => {
            if (!query.length) return callback([]);
            this.fetchHashtags(query, callback);
          },

          menuItemTemplate: item => item.original.name,

          selectTemplate: item => {
            return `${item.original?.display_name}`;
          }
        }
      ]
    });

    this.tribute.attach(quill.root);
  }

  hideOpenEmojiPicker() {
    const openDropdown = document.querySelector(
      "[data-controller=\"emoji-mart-dropdown\"]:has([data-emoji-mart-dropdown-target=\"menu\"]:not(.d-none))"
    );

    openDropdown?.dispatchEvent(
      new CustomEvent("emoji-mart-dropdown:hide", { bubbles: true })
    );
  }

  characterLength(quill) {
    if (!quill) return 0;

    const text = this.htmlToText(quill.root.innerHTML);
    return this.segments(text).length;
  }

  htmlToText(html) {
    const div = document.createElement("div");
    div.innerHTML = html;

    for (const node of div.querySelectorAll("span[data-native], span[data-emoji]")) {
      node.replaceWith(document.createTextNode(node.dataset.native || ""));
    }

    for (const img of div.querySelectorAll("img")) {
      img.replaceWith(document.createTextNode("◉"));
    }

    for (const br of div.querySelectorAll("br")) {
      br.replaceWith("\n");
    }

    for (const node of div.querySelectorAll("p, div, li")) {
      if (!node.textContent?.endsWith("\n")) {
        node.append(document.createTextNode("\n"));
      }
    }

    // Quill always adds one trailing newline, including for an empty editor.
    // Remove only that implicit newline so user-entered newlines still count.
    return (div.textContent || "").replaceAll("\u00A0", " ").replace(/\n$/, "");
  }

  segments(text) {
    if (!text) return [];

    if (!this._segmenter) {
      this._segmenter = new Intl.Segmenter(undefined, {
        granularity: "grapheme"
      });
    }

    return [...this._segmenter.segment(text)].map((p) => p.segment);
  }

  dispatchContentStateUpdate(quill) {
    const scope = this.element.closest("[data-controller*=\"submit-state\"]");
    if (!scope) return;

    const index = this.element.dataset?.index;
    const pollPost = $(scope).find(`#post-poll-${index}`);

    const text = this.htmlToText(quill.root.innerHTML);
    const length = this.segments(text).length;
    const hasContent = pollPost.hasClass("d-none") ? text.trim().length > 0 : true;

    document.dispatchEvent(
      new CustomEvent("form:content-changed", {
        detail: {
          scope,
          hasContent,
          length
        }
      })
    );
  }

  updateContentState() {
    const quill = this.quill;
    if (!quill) return;

    this.dispatchContentStateUpdate(quill);
  }

  dispatchProgressUpdate(quill) {
    this.element.dispatchEvent(
      new CustomEvent("quill:progress", {
        bubbles: true,
        detail: {
          length: this.characterLength(quill),
          max: this.maxLimit
        }
      })
    );
  }

  dispatchImages(files) {
    const eventName = this.imageEventValue;
    if (!eventName) return false;

    this.element.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: { files }
      })
    );

    return true;
  }

  clear() {
    if (!this.quill) return;

    this.quill.setContents([]);

    if (this.hiddenInput) {
      this.hiddenInput.value = "";
      this.hiddenInput.dispatchEvent(new Event("input", { bubbles: true }));
    }

    const form = $(this.element).closest("form");
    if (form) {
      form.find("#thumb_url").val("");
      form.find("#link_preview").val(false);
      form.find("#link-thumb").empty();
    }

    clearTimeout(this.linkPreviewTimer);
    this.linkPreviewTimer = null;
  }

  disconnect() {
    globalThis.cancelAnimationFrame(this.emojiSelectionFrame);
    this.emojiSelectionFrame = null;
    clearTimeout(this.linkPreviewTimer);
    this.linkPreviewTimer = null;
    document.removeEventListener(
      "itoons:language-change",
      this.handleLanguageChange
    );
    $(this.element).off(".quillController");

    if (this.quill?.root && this._pasteHandler) {
      this.quill.root.removeEventListener(
        "paste",
        this._pasteHandler,
        true
      );
    }

    if (this.quill?.root && this._emojiCopyHandler) {
      this.quill.root.removeEventListener("copy", this._emojiCopyHandler);
    }

    if (this._nativeSelectionHandler) {
      document.removeEventListener("selectionchange", this._nativeSelectionHandler);
    }

    this.tribute?.detach?.(this.quill?.root);
    this.placeholderObserver?.disconnect();

    this.quill = null;
    this.hiddenInput = null;
    this.linkThumbObserver?.disconnect();
    this.linkThumbObserver = null;
    this.placeholderObserver = null;
  }

  autoLinkUrls(quill) {
    if (this.isAutoLinking) return;

    this.isAutoLinking = true;

    try {
      /*
      * Remove broken link fragments.
      *
      * Example:
      * <a href="full-url">8</a>
      *
      * The text "8" is not a valid URL, so remove its link format.
      */
      const links = quill.root.querySelectorAll(
        "a:not(.tribute-inserted):not(.atwho-inserted)"
      );

      for (const link of links) {
        const text = link.textContent.trim();

        if (/^https?:\/\/\S+$/i.test(text)) {
          continue;
        }

        const blot = this.Quill.find(link);
        if (!blot) continue;

        const index = quill.getIndex(blot);
        const length = link.textContent.length;

        quill.formatText(
          index,
          length,
          "link",
          false,
          "silent"
        );
      }

      /*
      * Find complete URLs in the editor and apply the
      * correct link format.
      */
      const text = quill.getText(
        0,
        Math.max(quill.getLength() - 1, 0)
      );

      const urlRegex = /https?:\/\/[^\s]+/gi;

      for (const match of text.matchAll(urlRegex)) {
        const url = match[0];
        const index = match.index;

        const formats = quill.getFormat(
          index,
          url.length
        );

        if (formats.link === url) continue;

        quill.formatText(
          index,
          url.length,
          {
            link: url
          },
          "silent"
        );
      }
    } finally {
      this.isAutoLinking = false;
    }
  }

  // Toggle editor height when the link preview changes.
  setupLinkThumbObserver() {
    if (!this.element.classList.contains("post-comment-box")) return;

    const wrapper = this.element.closest(".post-form-wrapper");
    const linkThumb = wrapper?.querySelector("#link-thumb");
    if (!linkThumb) return;

    const updateEditorClass = () => {
      const hasPreview = linkThumb.innerHTML.trim().length > 0;
      this.quill?.root.classList.toggle("min-h-120px", !hasPreview);
    };

    updateEditorClass();

    this.linkThumbObserver = new MutationObserver(updateEditorClass);

    this.linkThumbObserver.observe(linkThumb, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  fetchMentionUsers(query, callback) {
    fetch(`/posts/get_mention_users?query=${encodeURIComponent(query)}`)
      .then(res => res.json())
      .then(data => callback(data || []))
      .catch(() => callback([]));
  }

  fetchHashtags(query, callback) {
    fetch(`/posts/get_hashtag?query=${encodeURIComponent(query)}`)
      .then(res => res.json())
      .then(data => callback(data || []))
      .catch(() => callback([]));
  }

  focusInput() {
    if (!this.quill) return;

    requestAnimationFrame(() => {
      this.quill.focus({ preventScroll: true });

      const length = this.quill.getLength();
      this.quill.setSelection(length - 1, 0, "silent");
    });
  }
}

async function createThumb(url, element) {
  if (isIgnoredMessageUrl(url.href)) {
    clearLinkPreview(element);
    return;
  }

  const form = $(element).closest("form");
  const requestedUrl = url.href;

  if (
    form.find("#thumb_url").val() === requestedUrl &&
    form.find("#link-thumb").html().trim() !== ""
  ) return;
  let isMessageForm = $(element).data("name") === "message[body]";
  try {
    let metaInformation;

    if (globalThis.__linkPreviewInFlight[url.href]) {
      metaInformation = await globalThis.__linkPreviewInFlight[url.href];
    } else {
      globalThis.__linkPreviewInFlight[url.href] = fetch("/link_preview", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ url: url.href })
      })
        .then((res) => res.json())
        .finally(() => {
          delete globalThis.__linkPreviewInFlight[url.href];
        });
      metaInformation = await globalThis.__linkPreviewInFlight[url.href];
    }

    if (
      !metaInformation ||
      metaInformation["title"] === "Error" ||
      form.find("#thumb_url").val() !== requestedUrl
    )
      return;

    if (isMessageForm) {
      const linksInput = form.find("#message_links_data");

      let imageLinks = [];
      try {
        imageLinks = JSON.parse(linksInput.val() || "[]");
      } catch {
        imageLinks = [];
      }

      const alreadyAdded = imageLinks.some((l) => l.url === url.href);
      if (alreadyAdded) return;

      imageLinks.push({
        url: url.href,
        image: metaInformation.images || null,
        title: metaInformation.title || "",
        description: metaInformation.description || ""
      });

      linksInput.val(JSON.stringify(imageLinks));
      return;
    } else if (globalThis.location.pathname.includes("messages")) {
      const commonHtml =
        "<div class='preview-link mt-12 position-relative rounded-3 overflow-hidden bg-black w-100 mb-3'>" +
        "<button class='btn p-0 remove-field-button position-absolute top-0 end-0 m-2 z-2' type='button' data-action='message-preview#removeLinkPreview'>" +
        "<i class='ri-close-circle-line text-danger fs-22'></i>" +
        "</button>" +
        "<div class='d-flex align-items-center justify-content-start gap-3 p-2'>" +
        "<div class='w-110px h-110px position-relative rounded-2 overflow-hidden flex-shrink-0'>" +
        "<img class='d-block object-fit-contain img-fluid w-100 h-100 z-1 position-relative' src='" +
        metaInformation["images"] +
        "' " +
        "alt='" +
        metaInformation["title"] +
        "'>" +
        "<img class='d-block object-fit-cover position-absolute top-0 start-0 filter-blur-5 img-fluid w-100 h-100 scale-1x2' src='" +
        metaInformation["images"] +
        "' " +
        "alt='" +
        metaInformation["title"] +
        "'>" +
        "</div>" +
        "<div class='preview-link-desc d-flex flex-column align-items-baseline justify-content-center w-100 text-start z-1'>";
      const conditionalHtml = metaInformation["description"]
        ? "<h6 class='card-title empty-tag max-line-1 text-white-paint text-black fs-18 fw-bold mb-1 lh-base w-100 text-break'>" +
        metaInformation["title"] +
        "</h6>" +
        "<p class='card-text empty-tag max-line-1 text-white w-100 text-break'>" +
        metaInformation["description"] +
        "</p>"
        : "<h6 class='card-title empty-tag max-line-1 text-white-paint text-black fs-18 fw-bold mb-1 lh-base w-100 text-break'>" +
        metaInformation["title"] +
        "</h6>";
      const html = commonHtml + conditionalHtml + "</div></div></div>";
      form.find("#thumb_url").val(url.href);
      form.find("#link_preview").val(true);
      form.find("#link-thumb").html(html);
    } else {
      const commonHtml =
        "<div class='preview-link mt-12 position-relative overflow-hidden w-100'>" +
        "<button class='btn p-0 remove-field-button position-absolute top-0 end-0 m-3 z-2' type='button' data-action='strip-post#removePreview'>" +
        "<i class='ri-close-circle-line text-danger fs-22'></i>" +
        "</button>" +
        "<div class='h-lg-300px h-180px position-relative bg-black overflow-hidden rounded-3'>" +
        "<img class='d-block object-fit-contain image img-fluid w-100 h-100 rounded-inherit link-image z-1 position-relative' src='" +
        metaInformation["images"] +
        "' " +
        "alt='" +
        metaInformation["title"] +
        "'>" +
        "<img class='d-block object-fit-cover position-absolute top-0 start-0 filter-blur-10 image img-fluid w-100 h-100 rounded-inherit link-image scale-1x2' src='" +
        metaInformation["images"] +
        "' " +
        "alt='" +
        metaInformation["title"] +
        "'></div>" +
        "<div class='preview-link-desc position-absolute d-flex flex-column align-items-baseline justify-content-center w-100 bottom-0 start-0 text-start p-2 z-1'>";
      const conditionalHtml = metaInformation["description"]
        ? "<h6 class='card-title empty-tag max-line-1 transparent-dark text-white-paint text-black p-2 rounded-2 fs-18 fw-bold mb-1 lh-base w-100'>" +
        metaInformation["title"] +
        "</h6>" +
        "<p class='card-text empty-tag max-line-1 transparent-dark text-white p-2 rounded-2 w-100'>" +
        metaInformation["description"] +
        "</p>"
        : "<h6 class='card-title empty-tag max-line-1 transparent-dark text-white-paint text-black p-2 rounded-2 fs-18 fw-bold mb-1 lh-base w-100'>" +
        metaInformation["title"] +
        "</h6>";
      const html = commonHtml + conditionalHtml + "</div></div>";
      form.find("#thumb_url").val(url.href);
      form.find("#link_preview").val(true);
      form.find("#link-thumb").html(html);
    }
  } catch (e) {
    console.error(e);
  }
}

function isEmojiChar(char) {
  if (!char) return false;

  return (
    /\p{Extended_Pictographic}/u.test(char) ||
    /\p{Emoji_Presentation}/u.test(char) ||
    char.includes("\u20e3")
  );
}

function getUnified(char) {
  return Array.from(char)
    .map((c) => c.codePointAt(0).toString(16))
    .join("-");
}

function emojiUrl(unified) {
  return `https://emoji.itoons.com/emojis/${unified}.png`;
}

function nativeEmojiFromUnified(unified) {
  if (!unified) return "";

  try {
    return String.fromCodePoint(
      ...unified.split("-").map((value) => Number.parseInt(value, 16))
    );
  } catch {
    return "";
  }
}

function formatAndCollectLinks(quill, element) {
  const httpLinks = [];
  const links = quill.root.querySelectorAll("a");

  for (const link of links) {
    if (
      link.classList.contains("tribute-inserted") ||
      link.classList.contains("atwho-inserted")
    ) {
      return;
    }

    link.classList.add("text-primary");

    if (
      ["post[description]", "post[share_message]"].includes(
        element.dataset.name
      )
    ) {
      link.classList.add("post-desc-link");
    }

    httpLinks.push(link);
  }

  return httpLinks;
}

function handleLinks(quill, element) {
  const httpLinks = formatAndCollectLinks(quill, element);

  if (httpLinks?.length > 0) {
    const urlLink = httpLinks.at(-1);
    $(element).closest("form").find("#thumb_url").val(urlLink.href);
    createThumb(urlLink, element);
  } else {
    // Clear preview after link remove
    clearLinkPreview(element);
  }
}

function clearLinkPreview(element) {
  const form = $(element).closest("form");
  form.find("#thumb_url").val("");
  form.find("#link-thumb").html("");
  form.find("#link_preview").val(false);
}

function isIgnoredMessageUrl(value) {
  try {
    const url = new URL(value, globalThis.location.href);
    return /^\/messages(?:\/|$)/i.test(url.pathname);
  } catch {
    return false;
  }
}
